import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Skeleton
} from '@mui/material';
import { CheckCircleOutline, ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { scrollToFooterContact } from '../utils/scrollToFooter';
import { usePageImages } from '../hooks/usePageImages';

const APPLE_BG = '#f5f5f7';
const APPLE_BLUE = '#0071e3';

const WHY_CHOOSE_US = [
  'Manufacturing Top Quality Products',
  'Creating Unique Colours & Designs to bring out each team’s individuality',
  'Maximizing Athlete Performance',
  'Reaching the Global Market',
  'Exceeding Expectations',
  'Creating Innovative Product'
];

const applePillPrimary = {
  borderRadius: '980px',
  px: 2.5,
  py: 1.25,
  fontSize: '13px',
  lineHeight: 1.3,
  fontWeight: 400,
  textTransform: 'none',
  bgcolor: APPLE_BLUE,
  color: '#fff',
  boxShadow: 'none',
  width: 'auto',
  minHeight: 'unset',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': { bgcolor: '#0077ed', boxShadow: 'none' }
};

const applePillOutline = {
  borderRadius: '980px',
  px: 2.5,
  py: 1.25,
  fontSize: '13px',
  lineHeight: 1.3,
  fontWeight: 400,
  textTransform: 'none',
  width: 'auto',
  minHeight: 'unset',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  color: APPLE_BLUE,
  borderColor: APPLE_BLUE,
  borderWidth: 1,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(0,113,227,0.06)',
    borderColor: APPLE_BLUE,
    borderWidth: 1
  }
};

const sectionHeadingSx = {
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: '#1d1d1f',
  fontSize: { xs: '2rem', md: '2.75rem' },
  lineHeight: 1.08
};

const bodyTextSx = {
  color: '#6e6e73',
  lineHeight: 1.5,
  fontSize: { xs: '1.125rem', md: '1.25rem' }
};

function SplitSection({
  imageSrc,
  imageAlt,
  imageSide = 'right',
  bgcolor = 'transparent',
  imageLoading = false,
  children
}) {
  const imageFirst = imageSide === 'left';
  const [imageReady, setImageReady] = React.useState(false);

  React.useEffect(() => {
    setImageReady(false);
  }, [imageSrc]);

  const showPlaceholder = imageLoading || !imageSrc || !imageReady;

  return (
    <Box
      component="section"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        width: '100%',
        minHeight: { md: 'min(85vh, 820px)' },
        alignItems: 'stretch',
        bgcolor
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 5, md: 6, lg: 12 },
          py: { xs: 7, md: 8 },
          order: { xs: 2, md: imageFirst ? 2 : 1 }
        }}
      >
        <Box sx={{ maxWidth: 560, width: '100%' }}>{children}</Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 400, md: '100%' },
          width: '100%',
          overflow: 'hidden',
          order: { xs: 1, md: imageFirst ? 1 : 2 },
          bgcolor: 'rgba(0,0,0,0.06)'
        }}
      >
        {showPlaceholder && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0,0,0,0.08)',
              zIndex: 1
            }}
          />
        )}
        {!imageLoading && imageSrc && (
          <Box
            component="img"
            src={imageSrc}
            alt={imageAlt}
            onLoad={() => setImageReady(true)}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
              opacity: imageReady ? 1 : 0,
              transition: 'opacity 0.2s ease'
            }}
          />
        )}
      </Box>
    </Box>
  );
}

const About = () => {
  const { images: pageImages, loading: pageImagesLoading } = usePageImages();

  return (
    <Box sx={{ bgcolor: APPLE_BG, width: '100%', overflowX: 'hidden' }}>
      <SplitSection
        imageSrc={pageImages.about.mission}
        imageAlt="Zaror bespoke kits"
        imageSide="right"
        bgcolor="#fff"
        imageLoading={pageImagesLoading}
      >
        <Typography
          variant="overline"
          sx={{
            color: APPLE_BLUE,
            fontWeight: 600,
            letterSpacing: '0.08em',
            fontSize: '0.875rem'
          }}
        >
          Our mission
        </Typography>
        <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 3, mt: 1 }}>
          Premium bespoke sports clothing
        </Typography>
        <Typography sx={{ ...bodyTextSx, mb: 2 }}>
          Empowering teams to perform at their best, our mission at Zaror is to craft premium
          bespoke sports clothing that seamlessly blends innovation, style, and comfort. With
          meticulous attention to detail and a commitment to excellence, we strive to elevate
          the athletic experience.
        </Typography>
        <Typography sx={bodyTextSx}>
          Our dedication to quality craftsmanship and personalized service ensures that every
          piece of clothing we create embodies the spirit of individuality, enabling athletes
          to push boundaries, break records, and conquer challenges with confidence.
        </Typography>
      </SplitSection>

      <SplitSection
        imageSrc={pageImages.about.whyChoose}
        imageAlt="Team collaboration"
        imageSide="left"
        bgcolor={APPLE_BG}
        imageLoading={pageImagesLoading}
      >
          <Typography
            variant="overline"
            sx={{
              color: APPLE_BLUE,
              fontWeight: 600,
              letterSpacing: '0.08em',
              fontSize: '0.875rem'
            }}
          >
            Why choose us
          </Typography>
          <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 3, mt: 1 }}>
            Built for teams who expect more
          </Typography>
          <Stack spacing={1.75}>
            {WHY_CHOOSE_US.map((point, index) => (
              <Stack direction="row" spacing={1.5} alignItems="flex-start" key={index}>
                <CheckCircleOutline
                  sx={{ color: APPLE_BLUE, fontSize: 22, mt: 0.25, flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    color: '#1d1d1f',
                    fontSize: { xs: '1.0625rem', md: '1.125rem' },
                    lineHeight: 1.45
                  }}
                >
                  {point}
                </Typography>
              </Stack>
            ))}
          </Stack>
      </SplitSection>

      <SplitSection
        imageSrc={pageImages.about.manufacturing}
        imageAlt="Zaror manufacturing quality"
        imageSide="right"
        bgcolor="#fff"
        imageLoading={pageImagesLoading}
      >
        <Typography
          variant="overline"
          sx={{
            color: APPLE_BLUE,
            fontWeight: 600,
            letterSpacing: '0.08em',
            fontSize: '0.875rem'
          }}
        >
          Manufacturing
        </Typography>
        <Typography component="h2" sx={{ ...sectionHeadingSx, mb: 3, mt: 1 }}>
          Top quality products
        </Typography>
        <Typography sx={{ ...bodyTextSx, mb: 2 }}>
          Whether you’re a grassroots team, a collegiate powerhouse, or a professional club, we
          understand that every team has its own unique requirements and aspirations. That’s why
          we offer a comprehensive range of customization options, from design and colour
          selection to fabric choice and branding.
        </Typography>
        <Typography sx={{ ...bodyTextSx, mb: 3 }}>
          We’re not just in the business of making clothing — we’re in the business of building
          relationships and fostering success. Through exceptional customer service, timely
          delivery, and unwavering support, we strive to be a trusted partner in your team’s
          journey to greatness.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
          <Button
            variant="contained"
            disableElevation
            endIcon={<ArrowForward sx={{ fontSize: 13 }} />}
            sx={applePillPrimary}
            onClick={scrollToFooterContact}
          >
            Request a quote
          </Button>
          <Button component={Link} to="/football" variant="outlined" sx={applePillOutline}>
            Launch kit designer
          </Button>
        </Stack>
      </SplitSection>
    </Box>
  );
};

export default About;
